<template>
  <div class="partner-tab-sharing-level-popover">
    <div
      v-for="group in groups"
      :key="group.level"
      class="partner-tab-sharing-level-popover__group"
    >
      <BittsTag
        :color="LEVEL_GROUP[group.level].color"
        size="x-small"
        :slim="true"
        :text="LEVEL_GROUP[group.level].label"
      />
      <ul class="partner-tab-sharing-level-popover__list">
        <li v-for="population in group.populations" :key="population.id">
          <PresetFieldsTooltip
            :tooltip-data="population.sharedFields?.sources ?? null"
            :preset-name="population.sharedFields?.presetName"
          >
            <!-- Focus stays on the cell: its blur closes the popover before the click lands. -->
            <button
              type="button"
              class="partner-tab-sharing-level-popover__population"
              @mousedown.prevent
              @click="emit('open-partner-settings', population, 'popover_row')"
            >
              <BittsAvatar
                size="tiny"
                shape="square"
                :show-border="true"
                :custom-svg="`${population.integrationType}Icon`"
                class="partner-tab-sharing-level-popover__source"
              />
              <span class="partner-tab-sharing-level-popover__name">
                {{ population.name }}
              </span>
              <FontAwesomeIcon
                v-if="group.hasSharedFields"
                :icon="['fak', 'chevron-right']"
                class="partner-tab-sharing-level-popover__chevron"
              />
            </button>
          </PresetFieldsTooltip>
          <ul
            v-if="'overrides' in population && population.overrides.length"
            class="partner-tab-sharing-level-popover__overrides"
            data-testid="sharing-level-population-overrides"
          >
            <li v-for="(override, index) in population.overrides" :key="index">
              <button
                type="button"
                class="partner-tab-sharing-level-popover__override"
                @mousedown.prevent
                @click="
                  emit('open-partner-settings', population, 'popover_override')
                "
              >
                <BittsSvg
                  svg="cornerDownRight"
                  class="partner-tab-sharing-level-popover__override-arrow"
                />
                <span class="partner-tab-sharing-level-popover__name">
                  {{ overrideLabel(override) }}
                </span>
              </button>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsSvg, BittsTag } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';

import PresetFieldsTooltip from '@/components/sharing-settings/PresetFieldsTooltip.vue';

import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  type SharingType,
} from '@/constants/data_shares';

import usePartnerTabSharedFields from '../composables/usePartnerTabSharedFields';
import {
  SHARING_LEVEL_VARIANT,
  SHARING_LEVEL_VARIANT_LABEL,
} from '../sharingLevelVariantLabels';
import type {
  OutgoingPopulationSharingLevel,
  PopulationOverrideType,
  PopulationSharingLevel,
  SharingSideType,
} from '../types';

const { populations, side, partnerOrgId, partnerName } = defineProps<{
  populations: (PopulationSharingLevel | OutgoingPopulationSharingLevel)[];
  side: SharingSideType;
  partnerOrgId: number;
  partnerName: string;
}>();

const emit = defineEmits<{
  'open-partner-settings': [
    population: PopulationSharingLevel | OutgoingPopulationSharingLevel,
    entryPoint: 'popover_row' | 'popover_override',
  ];
}>();

function overrideLabel({
  level,
  partnerPopulationName,
}: PopulationOverrideType) {
  const levelLabel = SHARING_LEVEL_VARIANT_LABEL[SHARING_LEVEL_VARIANT[level]];
  return partnerPopulationName
    ? `${levelLabel} for ${partnerName} ${partnerPopulationName}`
    : `${levelLabel} for one of ${partnerName}'s populations`;
}

const { getSharedFields } = usePartnerTabSharedFields();

/** Figma order: most to least shared. */
const LEVEL_ORDER: SharingType[] = [
  GREENFIELD_SHARING,
  SHARING,
  OLAPS_ONLY,
  HIDDEN,
];

const LEVEL_GROUP: Record<SharingType, { label: string; color: string }> = {
  [GREENFIELD_SHARING]: { label: 'Full Population', color: 'info' },
  [SHARING]: { label: 'Overlapping Accounts', color: 'success' },
  [OLAPS_ONLY]: { label: 'Counts Only', color: 'warning' },
  [HIDDEN]: { label: 'Hidden', color: 'danger' },
};

const LEVELS_WITH_SHARED_FIELDS = new Set<SharingType>([
  GREENFIELD_SHARING,
  SHARING,
]);

const groups = computed(() =>
  LEVEL_ORDER.map((level) => {
    const hasSharedFields = LEVELS_WITH_SHARED_FIELDS.has(level);
    return {
      level,
      hasSharedFields,
      populations: populations
        .filter((population) => population.level === level)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((population) => ({
          ...population,
          sharedFields: hasSharedFields
            ? getSharedFields(side, population.id, partnerOrgId)
            : null,
        })),
    };
  }).filter((group) => group.populations.length),
);
</script>

<style lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-sharing-level-popover {
  @apply flex flex-col gap-8 p-8;
}
.partner-tab-sharing-level-popover__group {
  @apply flex flex-col items-start gap-4;

  .ant-tag {
    @apply px-6 mr-0;
  }
}
.partner-tab-sharing-level-popover__list {
  @apply flex flex-col w-full;
}
.partner-tab-sharing-level-popover__population {
  @apply flex items-center gap-6 w-full h-24 px-6 py-2 rounded-6 text-left text-sm text-neutral-text cursor-pointer;

  &:hover {
    @apply bg-neutral-background-weak;
  }
}
.partner-tab-sharing-level-popover__source {
  @apply shrink-0;
}
.partner-tab-sharing-level-popover__name {
  @apply flex-1 min-w-0 truncate;
}
.partner-tab-sharing-level-popover__overrides {
  @apply flex flex-col pl-12;
}
.partner-tab-sharing-level-popover__override {
  @apply flex items-center gap-4 w-full h-20 px-6 rounded-6 text-left text-xs text-neutral-text-weak cursor-pointer;

  &:hover {
    @apply bg-neutral-background-weak;
  }
}
.partner-tab-sharing-level-popover__override-arrow {
  @apply shrink-0 w-12 h-12;
}
.partner-tab-sharing-level-popover__chevron {
  @apply shrink-0 w-12 h-12 text-neutral-accent;
}
</style>
